/* ==========================================================================
   Metamorfosis Magazine — flipbook.js
   In-theme magazine reader. Reads like a book, stays on-domain.
   - CSS 3D rotateY page turns with a travelling shadow gradient
   - Drag-to-turn (desktop) / swipe (mobile)
   - Spreads on desktop, single page on mobile
   - Thumbnail strip, zoom, fullscreen, keyboard, focus trap
   - Lazy loads pages +/-2 from the current position
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.__flipbook) return;
    root.__flipbook = true;

    var stage    = root.querySelector('[data-fb-stage]');
    var pages    = Array.prototype.slice.call(root.querySelectorAll('[data-fb-page]'));
    var thumbs   = Array.prototype.slice.call(root.querySelectorAll('[data-fb-thumb]'));
    var label    = root.querySelector('[data-fb-label]');
    var btnPrev  = root.querySelector('[data-fb-prev]');
    var btnNext  = root.querySelector('[data-fb-next]');
    var btnFull  = root.querySelector('[data-fb-full]');
    var btnZoom  = root.querySelector('[data-fb-zoom]');
    var thumbBar = root.querySelector('[data-fb-thumbs]');

    if (!stage || !pages.length) return;

    var total = pages.length;
    var index = 0;                 /* index of the left page of the current spread */
    var zoom = 1;
    var spread = window.matchMedia('(min-width: 900px)').matches;

    /* ---------- lazy loading ---------- */
    function loadNear() {
      pages.forEach(function (p, i) {
        if (Math.abs(i - index) <= 2 || i === 0) {
          var img = p.querySelector('img[data-src]');
          if (img) { img.src = img.getAttribute('data-src'); img.removeAttribute('data-src'); }
        }
      });
    }

    /* ---------- layout ---------- */
    function layout() {
      spread = window.matchMedia('(min-width: 900px)').matches;
      root.classList.toggle('is-spread', spread);
      /* Page 1 is a cover: shown alone even in spread mode */
      pages.forEach(function (p, i) {
        var visible = spread
          ? (index === 0 ? i === 0 : (i === index || i === index + 1))
          : i === index;
        p.classList.toggle('is-visible', visible);
        p.setAttribute('aria-hidden', visible ? 'false' : 'true');
        p.classList.toggle('is-left', spread && visible && i === index && index !== 0);
        p.classList.toggle('is-right', spread && visible && i === index + 1);
        p.classList.toggle('is-solo', !spread || index === 0);
      });
      if (label) {
        label.textContent = spread && index !== 0 && index + 1 < total
          ? (index + 1) + '–' + (index + 2) + ' / ' + total
          : (index + 1) + ' / ' + total;
      }
      thumbs.forEach(function (t, i) {
        var on = spread && index !== 0 ? (i === index || i === index + 1) : i === index;
        t.classList.toggle('is-current', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
      if (btnPrev) btnPrev.disabled = index <= 0;
      if (btnNext) btnNext.disabled = index >= total - 1;
      loadNear();
      scrollThumbIntoView();
    }

    function scrollThumbIntoView() {
      if (!thumbBar || !thumbs[index]) return;
      var t = thumbs[index];
      var tl = t.offsetLeft, tw = t.offsetWidth;
      var bl = thumbBar.scrollLeft, bw = thumbBar.clientWidth;
      if (tl < bl || tl + tw > bl + bw) {
        thumbBar.scrollTo({ left: tl - bw / 2 + tw / 2, behavior: REDUCED ? 'auto' : 'smooth' });
      }
    }

    /* ---------- turning ---------- */
    var turning = false;
    function step() { return spread ? (index === 0 ? 1 : 2) : 1; }

    function go(dir) {
      if (turning) return;
      var target = dir > 0 ? index + step() : (spread ? Math.max(0, index - (index <= 2 ? index : 2)) : index - 1);
      if (dir > 0 && index >= total - 1) return;
      if (dir < 0 && index <= 0) return;
      target = Math.max(0, Math.min(total - 1, target));
      if (target === index) return;

      if (REDUCED) { index = target; layout(); return; }

      /* Animate the leaf that is leaving */
      var leaf = dir > 0
        ? (spread && index !== 0 ? pages[index + 1] : pages[index])
        : pages[target];
      turning = true;
      if (leaf) {
        leaf.classList.add(dir > 0 ? 'is-turning-fwd' : 'is-turning-back');
      }
      window.setTimeout(function () {
        if (leaf) leaf.classList.remove('is-turning-fwd', 'is-turning-back');
        index = target;
        layout();
        turning = false;
      }, 700);
    }

    function goTo(i) {
      if (turning) return;
      index = Math.max(0, Math.min(total - 1, spread && i > 0 && i % 2 === 0 ? i - 1 : i));
      layout();
    }

    if (btnNext) btnNext.addEventListener('click', function () { go(1); });
    if (btnPrev) btnPrev.addEventListener('click', function () { go(-1); });
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { goTo(i); }); });

    /* ---------- drag / swipe ---------- */
    var dragX = null;
    stage.addEventListener('pointerdown', function (e) { dragX = e.clientX; });
    stage.addEventListener('pointerup', function (e) {
      if (dragX === null) return;
      var dx = e.clientX - dragX;
      dragX = null;
      if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    });
    stage.addEventListener('pointercancel', function () { dragX = null; });

    /* ---------- zoom ---------- */
    function applyZoom() {
      stage.style.setProperty('--fb-zoom', zoom);
      root.classList.toggle('is-zoomed', zoom > 1);
      if (btnZoom) btnZoom.setAttribute('aria-pressed', String(zoom > 1));
    }
    if (btnZoom) {
      btnZoom.addEventListener('click', function () {
        zoom = zoom > 1 ? 1 : 1.6;
        applyZoom();
      });
    }

    /* ---------- fullscreen + focus trap ---------- */
    function setFull(on) {
      root.classList.toggle('is-full', on);
      document.body.classList.toggle('fb-locked', on);
      if (btnFull) btnFull.setAttribute('aria-pressed', String(on));
      if (on) root.focus();
    }
    if (btnFull) btnFull.addEventListener('click', function () { setFull(!root.classList.contains('is-full')); });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(total - 1); }
      else if (e.key === 'Escape' && root.classList.contains('is-full')) { setFull(false); }
      else if (e.key === 'Tab' && root.classList.contains('is-full')) {
        var f = root.querySelectorAll('button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    window.addEventListener('resize', function () {
      var wasSpread = spread;
      spread = window.matchMedia('(min-width: 900px)').matches;
      if (wasSpread !== spread) layout();
    }, { passive: true });

    layout();
    applyZoom();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-flipbook]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
